/**
 * Derivation layer for KPI Metrics Briefing view models — the KPI counterpart
 * to derive.ts (Public Data Briefings).
 *
 * ── Verification status (2026-09-14) ─────────────────────────────────────
 * Both `buildKpiRow`/`buildKpiColumns` (view=table) and `buildKpiSituationCard`
 * (view=category) are now CONFIRMED — read directly off real, fully-populated
 * 200 responses (Smarsh Org / TD Bank, dataset_kind=production, 2026-03-31),
 * not guessed. xfactor's own "Docs & API Library" page describes a differently
 * *named* 8-block DTO (Synopsis, ScoresAndDeltas, KeyActions, KeyInsights,
 * Risks, Opportunities, KeyTrends, MetricsEvidence) — the real field names
 * below are camelCase and shaped differently than that page implies.
 *
 * The real `render/kpi` (view=category) response wraps everything under a
 * `category` object: { view, dataset_kind, category: { categoryId,
 * categoryName, bubble: <ScoreObject, same shape as the table/row rows>,
 * synopsis, scores: {primary, risk, opportunity, divergenceRollup},
 * keyActions[], keyInsights[], keyHighlights[], keyLowlights[], risks[],
 * opportunities[], keyTrends[], metricNameById, metricEvidenceById,
 * populated: {...} } }. There is no single "quote"/one-liner field and no
 * numeric "confidence %" field the way Public's situationCard has — KPI's
 * closest analogs are `bubble.confidenceTier` (a band, not a percentage) and
 * `bubble.trendExplanation` (a paragraph, reused below as Outlook).
 */

import { isDict, pickScore } from "./derive";
import type {
  XFactorMove,
  XFactorPoint,
  XFactorScoreTile,
} from "../../../Types/XFactorViewsRendering";

export type XFactorKpiCategoryDetail =
  | Record<string, unknown>
  | null
  | undefined;

export type XFactorKpiSituationCardModel = {
  accountName: string;
  asOf: string | null;
  freshness: string | null;
  confidence: number | null;
  tiles: XFactorScoreTile[];
  narrative: string | null;
  quote: string | null;
  mustKnow: XFactorPoint[];
  watchOuts: XFactorPoint[];
  moves: XFactorMove[];
  outlook: string | null;
  evidence: XFactorPoint[];
  present: boolean;
};

const EMPTY_CARD = (accountName: string): XFactorKpiSituationCardModel => ({
  accountName,
  asOf: null,
  freshness: null,
  confidence: null,
  tiles: [],
  narrative: null,
  quote: null,
  mustKnow: [],
  watchOuts: [],
  moves: [],
  outlook: null,
  evidence: [],
  present: false,
});

const numOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

const strOrNull = (v: unknown): string | null =>
  typeof v === "string" && v ? v : null;

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const emptyPoint = (): XFactorPoint => ({
  title: null,
  body: null,
  tag: null,
  chip: null,
  implication: null,
  outcome: null,
  timeframe: null,
  refs: [],
});

/**
 * Pass-2 Situation Card bullets — xfactor shape with title + body + category
 * tag (RELATIONSHIPS / FINANCIAL / …). `_Passthrough` on the schema lets
 * title/explanation/source_category through even when only `text` is typed.
 */
const pointFromSituationBullet = (item: unknown): XFactorPoint => {
  if (!isDict(item)) return emptyPoint();
  const title =
    strOrNull(item.title) ??
    strOrNull(item.label) ??
    strOrNull(item.headline) ??
    strOrNull(item.text);
  const body =
    strOrNull(item.explanation) ??
    strOrNull(item.detail) ??
    strOrNull(item.body) ??
    (strOrNull(item.title) || strOrNull(item.label) || strOrNull(item.headline)
      ? strOrNull(item.text)
      : null);
  const tagRaw =
    strOrNull(item.source_category) ??
    strOrNull(item.source_section) ??
    strOrNull(item.category);
  return {
    ...emptyPoint(),
    title,
    body: body && body !== title ? body : null,
    tag: tagRaw ? tagRaw.replace(/_/g, " ").toUpperCase() : null,
  };
};

/** Highlight/risk wire items with optional explanation. */
const pointFromInsightOrRisk = (item: unknown): XFactorPoint => {
  if (!isDict(item)) return emptyPoint();
  const severity = strOrNull(item.severity);
  return {
    ...emptyPoint(),
    title: strOrNull(item.text),
    body: strOrNull(item.explanation),
    // Prefer category tag; severity alone becomes chip (avoid "HIGH HIGH").
    tag: strOrNull(item.source_category) ?? strOrNull(item.horizon),
    chip: severity,
  };
};

/** `keyActions[]` items — `{action, priority, actionScore, expectedOutcome, actionExplanation}`. */
const moveFromKeyAction = (item: unknown): XFactorMove => ({
  timeframe: null,
  action: isDict(item) ? strOrNull(item.action) : null,
  rationale: isDict(item) ? strOrNull(item.actionExplanation) : null,
  owner: null,
  outcome: isDict(item) ? strOrNull(item.expectedOutcome) : null,
  trigger: null,
  tag: isDict(item)
    ? (strOrNull(item.source_category) ?? strOrNull(item.priority))
    : null,
  priority: isDict(item) ? strOrNull(item.priority) : null,
  score: isDict(item) ? numOrNull(item.actionScore) : null,
});

/** Pass-2 `situationCard.moves[]` — `{text|play, owner, when, source_category}`. */
const moveFromSituationCardItem = (item: unknown): XFactorMove => {
  if (!isDict(item)) {
    return {
      timeframe: null,
      action: null,
      rationale: null,
      owner: null,
      outcome: null,
      trigger: null,
      tag: null,
      priority: null,
      score: null,
    };
  }
  const fromAction = moveFromKeyAction(item);
  const tagRaw =
    strOrNull(item.source_category) ??
    strOrNull(item.source_section) ??
    strOrNull(item.category) ??
    fromAction.tag;
  return {
    ...fromAction,
    action: fromAction.action ?? strOrNull(item.text) ?? strOrNull(item.play),
    owner: strOrNull(item.owner) ?? fromAction.owner,
    timeframe:
      strOrNull(item.when) ?? strOrNull(item.timeframe) ?? fromAction.timeframe,
    tag: tagRaw ? tagRaw.replace(/_/g, " ").toUpperCase() : null,
  };
};

/**
 * Resolve the category/overall node from either wire shape:
 * - external xfactor: `{ category: { bubble, keyInsights, … } }`
 * - native EB: `{ briefing: { overall: { bubble, keyHighlights, … } } }`
 *              or nested `{ briefing: { briefing: { overall } } }`
 */
const resolveKpiCategoryNode = (
  data: Record<string, unknown>
): {
  category: Record<string, unknown>;
  asOf: string | null;
  siblingCategories: unknown[];
} => {
  if (isDict(data.category)) {
    return {
      category: data.category,
      asOf: null,
      siblingCategories: asArray(data.categories),
    };
  }

  let briefing: unknown = data.briefing;
  if (
    isDict(briefing) &&
    isDict(briefing.briefing) &&
    (isDict((briefing.briefing as Record<string, unknown>).overall) ||
      isDict((briefing.briefing as Record<string, unknown>).category))
  ) {
    briefing = briefing.briefing;
  }

  if (isDict(briefing)) {
    const asOf = strOrNull(briefing.measurementDate);
    const siblingCategories = asArray(briefing.categories);
    if (isDict(briefing.overall)) {
      return { category: briefing.overall, asOf, siblingCategories };
    }
    if (isDict(briefing.category)) {
      return { category: briefing.category, asOf, siblingCategories };
    }
  }

  return {
    category: data,
    asOf: null,
    siblingCategories: asArray(data.categories),
  };
};

/**
 * `metricNameById` (id -> label) + `metricEvidenceById` (id -> {value, uom}),
 * two parallel dicts rather than one array. Skips metrics with no observed
 * value so the Evidence list isn't dozens of blank rows.
 */
const evidenceFromMetricDicts = (
  namesById: unknown,
  evidenceById: unknown
): XFactorPoint[] => {
  if (!isDict(namesById) || !isDict(evidenceById)) return [];
  const points: XFactorPoint[] = [];
  for (const metricId of Object.keys(namesById)) {
    const evidence = evidenceById[metricId];
    if (!isDict(evidence) || evidence.value == null) continue;
    const label = strOrNull(namesById[metricId]) || metricId;
    const uom = strOrNull(evidence.uom);
    const norm =
      evidence.normalizedScore != null
        ? ` (norm.: ${evidence.normalizedScore})`
        : evidence.norm != null
          ? ` (norm.: ${evidence.norm})`
          : "";
    points.push({
      ...emptyPoint(),
      title: label,
      body: `${evidence.value}${uom ? ` ${uom}` : ""}${norm}`,
    });
  }
  return points;
};

/** Overall Situation Card evidence — pull from sibling category metrics. */
const evidenceFromCategories = (categories: unknown[]): XFactorPoint[] => {
  const points: XFactorPoint[] = [];
  const seen = new Set<string>();
  for (const cat of categories) {
    if (!isDict(cat)) continue;
    for (const metric of asArray(cat.metrics)) {
      if (!isDict(metric)) continue;
      const id = strOrNull(metric.metricId) ?? strOrNull(metric.metric_id);
      const label =
        strOrNull(metric.displayName) ?? strOrNull(metric.name) ?? id;
      if (!label || (id && seen.has(id))) continue;
      if (id) seen.add(id);
      const value = metric.actualValue ?? metric.value;
      if (value == null) continue;
      const uom = strOrNull(metric.uom) ?? strOrNull(metric.unit);
      const norm = numOrNull(metric.normalizedScore) ?? numOrNull(metric.norm);
      points.push({
        ...emptyPoint(),
        title: label,
        body: `${value}${uom ? ` ${uom}` : ""}${
          norm != null ? ` (norm.: ${norm})` : ""
        }`,
      });
    }
  }
  return points;
};

const joinRationales = (...parts: Array<string | null>): string | null => {
  const cleaned = parts.map(p => (p || "").trim()).filter(Boolean);
  if (!cleaned.length) return null;
  return cleaned.join(" ");
};

/**
 * The KPI Situation Card — same one-screen shape as Public Data's
 * buildSituationCard: score strip, why-these-scores narrative, meeting
 * one-liner quote, Must Know / Watch-outs / Moves, Outlook, Evidence.
 *
 * Prefer Pass-2 `situationCard` (xfactor synthesis) when present; fall back
 * to overall highlights/risks/actions and bubble scores.
 */
export const buildKpiSituationCard = (
  accountName: string,
  data?: XFactorKpiCategoryDetail
): XFactorKpiSituationCardModel => {
  if (!isDict(data)) return EMPTY_CARD(accountName);

  const { category, asOf, siblingCategories } = resolveKpiCategoryNode(data);
  const bubble = isDict(category.bubble) ? category.bubble : {};
  const pass2 = isDict(category.situationCard) ? category.situationCard : null;
  const strip = pass2 && isDict(pass2.scoreStrip) ? pass2.scoreStrip : null;
  const rationales = isDict(category.scoreRationales)
    ? category.scoreRationales
    : {};
  const scoresPrimary = isDict(category.scores)
    ? isDict(category.scores.primary)
      ? category.scores.primary
      : {}
    : {};

  // Pass-2 scoreStrip is the Situation Card face when present (xfactor).
  const health =
    (strip ? numOrNull(strip.health) : null) ??
    numOrNull(bubble.categoryScore) ??
    numOrNull(bubble.aiCategoryScore);
  const risk =
    (strip ? numOrNull(strip.risk) : null) ??
    numOrNull(bubble.riskScore) ??
    numOrNull(bubble.aiRiskScore) ??
    numOrNull(bubble.mechRiskScore);
  const opportunity =
    (strip ? numOrNull(strip.opportunity) : null) ??
    numOrNull(bubble.opportunityScore) ??
    numOrNull(bubble.aiOpportunityScore) ??
    numOrNull(bubble.mechOpportunityScore);

  const hasBubbleScores = health != null || risk != null || opportunity != null;

  const mustKnowFromPass2 = pass2
    ? asArray(pass2.mustKnow)
        .map(pointFromSituationBullet)
        .filter(p => p.title)
    : [];
  const mustKnowFromInsights = asArray(category.keyInsights)
    .map(pointFromInsightOrRisk)
    .filter(p => p.title);
  const mustKnowFromHighlights = asArray(category.keyHighlights)
    .map(pointFromInsightOrRisk)
    .filter(p => p.title);
  const mustKnow =
    mustKnowFromPass2.length > 0
      ? mustKnowFromPass2
      : mustKnowFromInsights.length > 0
        ? mustKnowFromInsights
        : mustKnowFromHighlights;

  const watchOutsFromPass2 = pass2
    ? asArray(pass2.watchOuts)
        .map(pointFromSituationBullet)
        .filter(p => p.title)
    : [];
  const watchOutsFromRisks = asArray(category.risks)
    .map(pointFromInsightOrRisk)
    .filter(p => p.title);
  const watchOuts =
    watchOutsFromPass2.length > 0 ? watchOutsFromPass2 : watchOutsFromRisks;

  const movesFromPass2 = pass2
    ? asArray(pass2.moves)
        .map(moveFromSituationCardItem)
        .filter(m => m.action)
    : [];
  const movesFromActions = asArray(category.keyActions)
    .map(moveFromKeyAction)
    .filter(m => m.action);
  const moves = movesFromPass2.length > 0 ? movesFromPass2 : movesFromActions;

  // Public Data: narrative = whyTheseScores under the strip; quote = meetingOneLiner.
  const quote = pass2 ? strOrNull(pass2.meetingOneLiner) : null;
  const narrative =
    (strip ? strOrNull(strip.whyTheseScores) : null) ??
    joinRationales(
      strOrNull(rationales.health) ?? strOrNull(scoresPrimary.rationale),
      strOrNull(rationales.risk),
      strOrNull(rationales.opportunity)
    ) ??
    (quote ? null : strOrNull(category.synopsis));

  const outlook =
    (pass2 ? strOrNull(pass2.outlook) : null) ??
    strOrNull(bubble.trendExplanation) ??
    strOrNull(category.crossCategoryTrend);

  let evidence = evidenceFromMetricDicts(
    category.metricNameById,
    category.metricEvidenceById
  );
  if (!evidence.length) {
    evidence = evidenceFromCategories(siblingCategories);
  }

  const present =
    hasBubbleScores ||
    Boolean(narrative) ||
    Boolean(quote) ||
    mustKnow.length > 0 ||
    watchOuts.length > 0 ||
    moves.length > 0 ||
    Boolean(outlook) ||
    evidence.length > 0;

  if (!present) return EMPTY_CARD(accountName);

  const tiles: XFactorScoreTile[] = [
    {
      label: "Health",
      value: health,
      rationale:
        strOrNull(rationales.health) ?? strOrNull(scoresPrimary.rationale),
    },
    {
      label: "Risk",
      value: risk,
      rationale: strOrNull(rationales.risk),
    },
    {
      label: "Opportunity",
      value: opportunity,
      rationale: strOrNull(rationales.opportunity),
    },
  ];

  return {
    accountName,
    asOf,
    freshness:
      (pass2 && isDict(pass2.header)
        ? strOrNull(pass2.header.freshness)
        : null) ?? strOrNull(bubble.confidenceTier),
    confidence: null,
    tiles,
    narrative,
    quote,
    mustKnow,
    watchOuts,
    moves,
    outlook,
    evidence,
    present: true,
  };
};

/* ------------------------------------------------------------------ */
/* Portfolio matrix row (render/kpi view=table) — additive, plug-in     */
/* alongside Hawkeye's own matrix, not a replacement for it.            */
/*                                                                       */
/* Schema below is CONFIRMED, not guessed — read directly off a live    */
/* 200 response from xfactor's render/kpi (view=table) on 2026-09-14:   */
/*   { view, dataset_kind,                                              */
/*     columns: [{categoryId,name,enabled,displayOrder,source,          */
/*                effectiveWeightPct}],                                 */
/*     rows: [{account:{id,name,industry_sector,tier_segment,region},   */
/*             hasSelectedDateData, measurementDate,                    */
/*             overall: <ScoreObject>,                                  */
/*             categories: [<ScoreObject> per column],                  */
/*             resultsetId}] }                                          */
/* <ScoreObject> carries categoryScore/riskScore/opportunityScore (not  */
/* "score"), plus hasData/emptyReason — emptyReason:"missing_inputs" is */
/* a real "not scored yet" state upstream, not an error on our side.    */
/* KPI_CATEGORY_ORDER is NOT used here — the org's actual column set    */
/* (from `columns`) is the source of truth, and it does not necessarily */
/* match Hawkeye's own category list one-for-one (e.g. this org's       */
/* xfactor config has no "relationships" column, unlike Hawkeye's).     */
/* ------------------------------------------------------------------ */

export type XFactorKpiColumn = {
  categoryId: string;
  name: string;
  /** The category's weight in the overall score, as configured. */
  weight: number | null;
};

export type XFactorKpiCategoryScore = {
  id: string;
  label: string;
  hasData: boolean;
  score: number | null;
};

export type XFactorKpiRowModel = {
  accountId: string;
  hasData: boolean;
  overall: XFactorKpiCategoryScore | null;
  categories: XFactorKpiCategoryScore[];
  present: boolean;
};

const EMPTY_ROW = (accountId: string): XFactorKpiRowModel => ({
  accountId,
  hasData: false,
  overall: null,
  categories: [],
  present: false,
});

const scoreObjectToCategoryScore = (
  id: string,
  label: string,
  raw: unknown
): XFactorKpiCategoryScore => ({
  id,
  label,
  hasData: isDict(raw) ? Boolean(raw.hasData) : false,
  score: isDict(raw) ? pickScore(raw, ["categoryScore"]) : null,
});

/** `columns` from the same render/kpi (view=table) response, for labels/order. */
export const buildKpiColumns = (rawColumns: unknown): XFactorKpiColumn[] =>
  (Array.isArray(rawColumns) ? rawColumns : [])
    .filter(isDict)
    .map(c => ({
      categoryId: String(c.categoryId ?? ""),
      name: String(c.name ?? c.categoryId ?? ""),
      weight: numOrNull(c.effectiveWeightPct),
    }))
    .filter(c => c.categoryId);

/** One portfolio-matrix row (one account across every column). */
export const buildKpiRow = (
  accountId: string,
  row: Record<string, unknown> | null | undefined,
  columns: XFactorKpiColumn[]
): XFactorKpiRowModel => {
  if (!isDict(row)) return EMPTY_ROW(accountId);

  const rawCategories = Array.isArray(row.categories) ? row.categories : [];
  const byId = new Map<string, unknown>();
  for (const raw of rawCategories) {
    if (isDict(raw) && raw.categoryId) byId.set(String(raw.categoryId), raw);
  }

  return {
    accountId,
    hasData: Boolean(row.hasSelectedDateData),
    overall: row.overall
      ? scoreObjectToCategoryScore("overall", "Overall", row.overall)
      : null,
    categories: columns.map(col =>
      scoreObjectToCategoryScore(
        col.categoryId,
        col.name,
        byId.get(col.categoryId)
      )
    ),
    present: true,
  };
};

/* ------------------------------------------------------------------ */
/* Category detail view (render/kpi view=full) — the full click-through */
/* briefing xfactor's own "Views Rendering" tool shows when you click a  */
/* bubble: Highlights/Lowlights/Opportunities/Risks/Actions/Trends, each */
/* claim's cited metrics + missing-data anomaly flag, and per-metric     */
/* trend. Confirmed 2026-09-15 by reading a live view=full response      */
/* directly (not guessed) — every field below is real.                   */
/* ------------------------------------------------------------------ */

export type XFactorKpiMetricRef = { metricId: string; period: string | null };

const metricRefsFrom = (raw: unknown): XFactorKpiMetricRef[] =>
  asArray(raw)
    .filter(isDict)
    .map(r => ({
      metricId: strOrNull(r.metric_id) || "",
      period: strOrNull(r.period),
    }))
    .filter(r => r.metricId);

type XFactorKpiAnomaly = {
  flag: boolean;
  type: string | null;
  reason: string | null;
};

const anomalyFrom = (raw: unknown): XFactorKpiAnomaly => ({
  flag: isDict(raw) ? Boolean(raw.flag) : false,
  type: isDict(raw) ? strOrNull(raw.type) : null,
  reason: isDict(raw) ? strOrNull(raw.reason) : null,
});

/** `keyHighlights[]` / `keyLowlights[]` items. */
export type XFactorKpiInsightClaim = {
  text: string;
  explanation: string | null;
  confidence: string | null;
  supportingMetricRefs: XFactorKpiMetricRef[];
  anomaly: XFactorKpiAnomaly;
};

/** `risks[]` items — carries severity/likelihood/horizon, no `explanation`. */
export type XFactorKpiRiskClaim = {
  text: string;
  severity: string | null;
  likelihood: string | null;
  horizon: string | null;
  supportingMetricRefs: XFactorKpiMetricRef[];
  anomaly: XFactorKpiAnomaly;
};

/** `opportunities[]` items — carries potential/effort, no `explanation`. */
export type XFactorKpiOpportunityClaim = {
  text: string;
  potential: string | null;
  effort: string | null;
  supportingMetricRefs: XFactorKpiMetricRef[];
  anomaly: XFactorKpiAnomaly;
};

/** `keyActions[]` items. */
export type XFactorKpiActionItem = {
  action: string;
  priority: string | null;
  actionScore: number | null;
  expectedOutcome: string | null;
  actionExplanation: string | null;
  supportingMetricRefs: XFactorKpiMetricRef[];
  anomaly: XFactorKpiAnomaly;
};

export type XFactorKpiTrendSource =
  | "trend_insight"
  | "insight_trend_flag"
  | "metric_top_mover"
  | "cross_category_rollup";

/** `keyTrends[]` items — `source` distinguishes a narrative from a per-metric mover. */
export type XFactorKpiTrendItem = {
  text: string;
  source: XFactorKpiTrendSource;
  direction: string | null;
  magnitudePct: number | null;
  confidence: string | null;
  isTopMover: boolean;
  /** No numeric backing — xfactor tags these so the reader knows. */
  qualitative: boolean;
  citations: string[];
};

export type XFactorKpiMetricSeriesPoint = {
  date: string | null;
  value: number | null;
  normalizedValue: number | null;
};

/** One row of `category.metrics[]` — every configured metric, data or not. */
export type XFactorKpiMetricModel = {
  metricId: string;
  name: string;
  value: number | null;
  normalizedValue: number | null;
  weight: number | null;
  nominalWeight: number | null;
  uom: string | null;
  trendDirection: string | null;
  trendMagnitudePct: number | null;
  hasData: boolean;
  series: XFactorKpiMetricSeriesPoint[];
};

export type XFactorKpiScoreDivergenceRow = {
  categoryId: string;
  categoryName: string;
  mechanical: number | null;
  ai: number | null;
  delta: number | null;
  primaryDriver: string | null;
};

export type XFactorKpiCategoryDetailModel = {
  categoryId: string;
  categoryName: string;
  score: number | null;
  aiScore: number | null;
  mechScore: number | null;
  riskScore: number | null;
  opportunityScore: number | null;
  roCompositeScore: number | null;
  hasData: boolean;
  emptyReason: string | null;
  scoreRationale: string | null;
  divergenceExplanation: string | null;
  divergenceRollup: XFactorKpiScoreDivergenceRow[];
  synopsis: string | null;
  highlightsBrief: string | null;
  lowlightsBrief: string | null;
  roBrief: string | null;
  highlights: XFactorKpiInsightClaim[];
  lowlights: XFactorKpiInsightClaim[];
  opportunities: XFactorKpiOpportunityClaim[];
  risks: XFactorKpiRiskClaim[];
  actions: XFactorKpiActionItem[];
  trends: XFactorKpiTrendItem[];
  metrics: XFactorKpiMetricModel[];
  historyDepthUsed: number | null;
  /** Dates in the history window before the measurement date. */
  historyDates: string[];
  /** The category's own trend_insight (the Overall pane's per-category arrows). */
  trendDirection: string | null;
  trendMagnitudePct: number | null;
  trendConfidence: string | null;
  trendPeriodSpan: string | null;
  /** Category weight in the overall score, as assigned and as applied after rebalancing. */
  assignedWeightPct: number | null;
  effectiveWeightPct: number | null;
  /** Overall only: `cross_category_trend_insight`. */
  crossCategoryTrend: string | null;
  /** Every `*_supporting_metric_refs` on the synopsis and its briefs. */
  synopsisRefs: XFactorKpiMetricRef[];
};

export type XFactorKpiFullBriefing = {
  accountId: string | null;
  accountName: string | null;
  /** Measurement date. */
  date: string | null;
  /** When the narrative was generated; null when none exists yet. */
  generatedAt: string | null;
  overall: XFactorKpiCategoryDetailModel | null;
  categories: XFactorKpiCategoryDetailModel[];
};

const metricModelFrom = (raw: unknown): XFactorKpiMetricModel => {
  const m = isDict(raw) ? raw : {};
  return {
    metricId: strOrNull(m.metricId) || "",
    name: strOrNull(m.name) || strOrNull(m.metricId) || "",
    value: numOrNull(m.currentValue),
    normalizedValue: numOrNull(m.normalizedScore),
    weight: numOrNull(m.weightPct),
    nominalWeight: numOrNull(m.nominalWeightPct),
    uom: strOrNull(m.uom),
    trendDirection: strOrNull(m.trendDirection),
    trendMagnitudePct: numOrNull(m.trendMagnitudePct),
    hasData: numOrNull(m.normalizedScore) != null,
    series: asArray(m.series)
      .filter(isDict)
      .map(s => ({
        date: strOrNull(s.date),
        value: numOrNull(s.value),
        normalizedValue: numOrNull(s.normalized_value),
      })),
  };
};

const insightClaimFrom = (raw: unknown): XFactorKpiInsightClaim => {
  const item = isDict(raw) ? raw : {};
  return {
    text: strOrNull(item.text) || "",
    explanation: strOrNull(item.explanation),
    confidence: strOrNull(item.confidence),
    supportingMetricRefs: metricRefsFrom(item.supportingMetricRefs),
    anomaly: anomalyFrom(item.anomaly),
  };
};

const riskClaimFrom = (raw: unknown): XFactorKpiRiskClaim => {
  const item = isDict(raw) ? raw : {};
  return {
    text: strOrNull(item.text) || "",
    severity: strOrNull(item.severity),
    likelihood: strOrNull(item.likelihood),
    horizon: strOrNull(item.horizon),
    supportingMetricRefs: metricRefsFrom(item.supportingMetricRefs),
    anomaly: anomalyFrom(item.anomaly),
  };
};

const opportunityClaimFrom = (raw: unknown): XFactorKpiOpportunityClaim => {
  const item = isDict(raw) ? raw : {};
  return {
    text: strOrNull(item.text) || "",
    potential: strOrNull(item.potential),
    effort: strOrNull(item.effort),
    supportingMetricRefs: metricRefsFrom(item.supportingMetricRefs),
    anomaly: anomalyFrom(item.anomaly),
  };
};

const actionItemFrom = (raw: unknown): XFactorKpiActionItem => {
  const item = isDict(raw) ? raw : {};
  return {
    action: strOrNull(item.action) || "",
    priority: strOrNull(item.priority),
    actionScore: numOrNull(item.actionScore),
    expectedOutcome: strOrNull(item.expectedOutcome),
    actionExplanation: strOrNull(item.actionExplanation),
    supportingMetricRefs: metricRefsFrom(item.supportingMetricRefs),
    anomaly: anomalyFrom(item.anomaly),
  };
};

const TREND_SOURCES: XFactorKpiTrendSource[] = [
  "trend_insight",
  "insight_trend_flag",
  "metric_top_mover",
  "cross_category_rollup",
];

const trendItemFrom = (raw: unknown): XFactorKpiTrendItem => {
  const item = isDict(raw) ? raw : {};
  const rawSource = strOrNull(item.source) as XFactorKpiTrendSource | null;
  const source =
    rawSource && TREND_SOURCES.includes(rawSource)
      ? rawSource
      : "trend_insight";
  const citations = asArray(item.citations).map(c => String(c));
  return {
    text: strOrNull(item.text) || "",
    source,
    direction: strOrNull(item.direction),
    magnitudePct: numOrNull(item.magnitudePct),
    confidence: strOrNull(item.confidence),
    isTopMover: source === "metric_top_mover",
    qualitative: Boolean(item.qualitative),
    citations,
  };
};

const fmtSignedPct = (n: number | null): string => {
  if (n == null || !Number.isFinite(n)) return "";
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}${Math.round(Math.abs(n))}%`;
};

/**
 * Key Trends, composed the way xfactor's key-trends.core.ts does it: the
 * pane's own trend_insight, then the top three per-metric movers, then (on
 * Overall) the cross-category narrative and the three biggest AI-vs-
 * mechanical divergences. The backend doesn't emit `keyTrends` itself —
 * xfactor doesn't either; it composes them at read time from these parts.
 */
export const composeKpiKeyTrends = (
  detail: Pick<
    XFactorKpiCategoryDetailModel,
    | "categoryId"
    | "trendDirection"
    | "trendMagnitudePct"
    | "trendConfidence"
    | "trendPeriodSpan"
    | "metrics"
    | "crossCategoryTrend"
    | "divergenceRollup"
  >
): XFactorKpiTrendItem[] => {
  const out: XFactorKpiTrendItem[] = [];
  const item = (
    fields: Omit<XFactorKpiTrendItem, "isTopMover">
  ): XFactorKpiTrendItem => ({
    ...fields,
    isTopMover: fields.source === "metric_top_mover",
  });

  const direction = detail.trendDirection;
  const magnitude = detail.trendMagnitudePct;
  const span = detail.trendPeriodSpan;
  if (direction || magnitude != null || span) {
    const parts: string[] = [];
    if (span) parts.push(span);
    if (direction) {
      parts.push(
        `trend: ${
          direction.trim().toLowerCase() === "insufficient_data"
            ? "Insufficient Data"
            : direction
        }`
      );
    }
    if (magnitude != null) parts.push(fmtSignedPct(magnitude));
    out.push(
      item({
        source: "trend_insight",
        text: parts.length ? parts.join(" · ") : "Trend narrative",
        direction,
        magnitudePct: magnitude,
        confidence: detail.trendConfidence,
        qualitative: false,
        citations: [],
      })
    );
  }

  detail.metrics
    .filter(
      m => m.trendMagnitudePct != null && Number.isFinite(m.trendMagnitudePct)
    )
    .sort(
      (a, b) =>
        Math.abs(b.trendMagnitudePct as number) -
        Math.abs(a.trendMagnitudePct as number)
    )
    .slice(0, 3)
    .forEach(m =>
      out.push(
        item({
          source: "metric_top_mover",
          text: `${m.name}: ${fmtSignedPct(m.trendMagnitudePct)}${
            m.trendDirection ? ` (${m.trendDirection})` : ""
          }`,
          direction: m.trendDirection,
          magnitudePct: m.trendMagnitudePct,
          confidence: null,
          qualitative: false,
          citations: [m.metricId],
        })
      )
    );

  if (detail.categoryId === "overall") {
    if (detail.crossCategoryTrend && detail.crossCategoryTrend.trim()) {
      out.push(
        item({
          source: "cross_category_rollup",
          text: detail.crossCategoryTrend.trim(),
          direction: null,
          magnitudePct: null,
          confidence: null,
          qualitative: true,
          citations: [],
        })
      );
    }
    detail.divergenceRollup
      .filter(r => r.delta != null && Number.isFinite(r.delta))
      .sort((a, b) => Math.abs(b.delta as number) - Math.abs(a.delta as number))
      .slice(0, 3)
      .forEach(r =>
        out.push(
          item({
            source: "cross_category_rollup",
            text: `${r.categoryName || r.categoryId}: AI vs mechanical ${fmtSignedPct(
              r.delta
            )}${r.primaryDriver ? ` — ${r.primaryDriver}` : ""}`,
            direction: null,
            magnitudePct: r.delta,
            confidence: null,
            qualitative: true,
            citations: [],
          })
        )
      );
  }

  return out;
};

const divergenceRowFrom = (raw: unknown): XFactorKpiScoreDivergenceRow => {
  const item = isDict(raw) ? raw : {};
  return {
    categoryId: strOrNull(item.categoryId) || "",
    categoryName: strOrNull(item.categoryName) || "",
    mechanical: numOrNull(item.mechanical),
    ai: numOrNull(item.ai),
    delta: numOrNull(item.delta),
    primaryDriver: strOrNull(item.primaryDriver),
  };
};

/**
 * One category's full detail — same object whether it's `overall` or a
 * named category (both carry the same fields upstream). `divergenceRollup`
 * only comes from `overall.scores.divergenceRollup` (a portfolio-wide list);
 * for a named category it's filtered down to that category's own row so the
 * detail page's Q1 panel always shows one row scoped to what's open.
 */
const categoryDetailFrom = (
  raw: unknown,
  divergenceRollupOverride?: XFactorKpiScoreDivergenceRow[]
): XFactorKpiCategoryDetailModel => {
  const category = isDict(raw) ? raw : {};
  const bubble = isDict(category.bubble) ? category.bubble : {};
  const scores = isDict(category.scores) ? category.scores : {};
  const primary = isDict(scores.primary) ? scores.primary : {};
  const briefs = isDict(category.synopsisBriefs) ? category.synopsisBriefs : {};
  const rollup = asArray(scores.divergenceRollup).map(divergenceRowFrom);

  const detail: XFactorKpiCategoryDetailModel = {
    categoryId: strOrNull(category.categoryId) || "",
    categoryName: strOrNull(category.categoryName) || "",
    score: numOrNull(bubble.categoryScore),
    aiScore: numOrNull(bubble.aiCategoryScore),
    mechScore: numOrNull(bubble.mechCategoryScore),
    riskScore: numOrNull(bubble.riskScore),
    opportunityScore: numOrNull(bubble.opportunityScore),
    roCompositeScore: numOrNull(bubble.roCompositeScore),
    hasData: Boolean(bubble.hasData),
    emptyReason: strOrNull(bubble.emptyReason),
    scoreRationale:
      strOrNull(primary.rationale) || strOrNull(bubble.tooltipRationale),
    divergenceExplanation: strOrNull(bubble.categoryDivergenceExplanation),
    divergenceRollup: divergenceRollupOverride ?? rollup,
    synopsis: strOrNull(category.synopsis),
    highlightsBrief: strOrNull(briefs.highlightsBrief),
    lowlightsBrief: strOrNull(briefs.lowlightsBrief),
    roBrief: strOrNull(briefs.roBrief),
    highlights: asArray(category.keyHighlights).map(insightClaimFrom),
    lowlights: asArray(category.keyLowlights).map(insightClaimFrom),
    opportunities: asArray(category.opportunities).map(opportunityClaimFrom),
    risks: asArray(category.risks).map(riskClaimFrom),
    actions: asArray(category.keyActions).map(actionItemFrom),
    trends: asArray(category.keyTrends).map(trendItemFrom),
    metrics: asArray(category.metrics).map(metricModelFrom),
    historyDepthUsed: numOrNull(category.historyDepthUsed),
    historyDates: asArray(category.historyDates)
      .map(d => strOrNull(d))
      .filter((d): d is string => Boolean(d)),
    trendDirection: strOrNull(bubble.trendDirection),
    trendMagnitudePct: numOrNull(bubble.trendMagnitudePct),
    trendConfidence: strOrNull(bubble.trendConfidence),
    trendPeriodSpan: strOrNull(bubble.trendPeriodSpan),
    assignedWeightPct: numOrNull(bubble.assignedWeightPct),
    effectiveWeightPct: numOrNull(bubble.effectiveWeightPct),
    crossCategoryTrend: strOrNull(category.crossCategoryTrend),
    synopsisRefs: [
      ...metricRefsFrom(briefs.synopsisRefs),
      ...metricRefsFrom(briefs.highlightsBriefRefs),
      ...metricRefsFrom(briefs.lowlightsBriefRefs),
      ...metricRefsFrom(briefs.roBriefRefs),
    ],
  };
  if (!detail.trends.length) detail.trends = composeKpiKeyTrends(detail);
  return detail;
};

/**
 * Full account briefing (render/kpi view=full) — one call returns every
 * category plus `overall`, so switching categories in the detail modal is
 * instant client-side array navigation, no refetch.
 */
export const buildKpiFullBriefing = (
  data?: Record<string, unknown> | null
): XFactorKpiFullBriefing => {
  const briefing = isDict(data) ? data : {};
  const overallRaw = briefing.overall;
  const overallRollup = isDict(overallRaw)
    ? asArray(
        isDict(overallRaw.scores) ? overallRaw.scores.divergenceRollup : []
      ).map(divergenceRowFrom)
    : [];
  const rollupFor = (categoryId: string) =>
    overallRollup.filter(r => r.categoryId === categoryId);

  return {
    accountId: strOrNull(briefing.accountId),
    accountName: strOrNull(briefing.accountName),
    // The backend sends `measurementDate`; `date` is kept as a fallback for
    // any older payload. Reading only `date` left the modal with no date.
    date: strOrNull(briefing.measurementDate) || strOrNull(briefing.date),
    generatedAt: strOrNull(briefing.generatedAt),
    overall: overallRaw ? categoryDetailFrom(overallRaw, overallRollup) : null,
    categories: asArray(briefing.categories).map(raw =>
      categoryDetailFrom(
        raw,
        isDict(raw) ? rollupFor(String(raw.categoryId ?? "")) : undefined
      )
    ),
  };
};
